/**
 * CyberChef URL Import
 *
 * Parses CyberChef recipe URLs and converts them into our pipeline format.
 * Only maps operations we actually support — unmapped ops are tracked for
 * user feedback.
 *
 * CyberChef URL format:
 *   https://gchq.github.io/CyberChef/#recipe=Op_Name(args)Op2(args)&input=base64data
 *
 * - Operation names use underscores for spaces: `From_Base64`, `JSON_Beautify`
 * - Arguments are in parentheses, single-quoted strings, bare numbers/booleans
 * - Input data is base64-encoded after `&input=`
 */

import type { DeserializedPipeline } from './pipelineSharing';

// ---------------------------------------------------------------------------
// CyberChef operation → our toolId mapping
// ---------------------------------------------------------------------------

const CYBERCHEF_TO_TOOL: Record<string, string> = {
  // --- Encoding / Decoding ---
  'From_Base64': 'base64-decode',
  'To_Base64': 'base64-encode',
  'URL_Decode': 'url-decode',
  'URL_Encode': 'url-encode',
  'From_Hex': 'hex-decode',
  'To_Hex': 'hex-encode',
  'ROT13': 'rot13',
  'From_HTML_Entity': 'html-entity-decode',
  'To_HTML_Entity': 'html-entity-encode',
  'Encode_text': 'html-entity-encode',
  'Decode_text': 'html-entity-decode',
  'To_Morse_Code': 'morse-code',
  'From_Morse_Code': 'morse-code',
  'To_Binary': 'text-to-binary',
  'From_Binary': 'binary-to-text',

  // --- Formatting ---
  'JSON_Beautify': 'json-format',
  'JSON_Minify': 'json-minify',
  'XML_Beautify': 'xml-format',
  'XML_Minify': 'xml-minify',
  'CSS_Beautify': 'css-format',
  'CSS_Minify': 'css-minify',
  'JavaScript_Beautify': 'js-format',
  'JavaScript_Minify': 'js-minify',
  'SQL_Beautify': 'sql-format',
  'SQL_Minify': 'sql-minify',
  'YAML_Beautify': 'yaml-format',
  'YAML_Minify': 'yaml-minify',

  // --- Conversion ---
  'JSON_to_CSV': 'json-to-csv',
  'CSV_to_JSON': 'csv-to-json',
  'Render_Markdown': 'markdown-to-html',

  // --- Hashing ---
  'MD5': 'hash-generator',
  'SHA1': 'hash-generator',
  'SHA2': 'hash-generator',
  'SHA3': 'hash-generator',
  'HMAC': 'hmac-generator',

  // --- Text ---
  'Sort': 'sort-lines',
  'Unique': 'deduplicate-lines',
  'Reverse': 'reverse-text',
  'Find_/_Replace': 'find-replace',
  'Remove_whitespace': 'whitespace-remover',
  'Extract_email_addresses': 'extract-emails',
  'Extract_URLs': 'extract-urls',

  // --- Case ---
  'To_Upper_case': 'case-converter',
  'To_Lower_case': 'case-converter',

  // --- Generators ---
  'Generate_UUID': 'uuid-generator',
  'Generate_Lorem_Ipsum': 'lorem-ipsum',
};

// ---------------------------------------------------------------------------
// Parse result types
// ---------------------------------------------------------------------------

export interface CyberChefParseResult {
  pipeline: DeserializedPipeline;
  /** Operations that were successfully mapped */
  mappedCount: number;
  /** Operations that couldn't be mapped to our tools */
  skippedOperations: string[];
  /** Total operations found in the recipe */
  totalOperations: number;
}

/** A single parsed CyberChef operation. */
interface ParsedOperation {
  name: string;
  rawArgs: string;
}

// ---------------------------------------------------------------------------
// URL detection
// ---------------------------------------------------------------------------

/** Returns true if the string looks like a CyberChef URL or hash. */
export function isCyberChefUrl(url: string): boolean {
  return (
    url.includes('gchq.github.io/CyberChef') ||
    url.includes('cyberchef.org') ||
    // Hash-only: starts with #recipe=
    url.startsWith('#recipe=') ||
    // Full URL with recipe hash
    url.includes('#recipe=')
  );
}

// ---------------------------------------------------------------------------
// Recipe string parser
// ---------------------------------------------------------------------------

/**
 * Parse a CyberChef recipe string into individual operations.
 *
 * Recipe format: `Op_Name(arg1,arg2,'str arg')Op2()`
 * Operation names are sequences of word chars, underscores, and `/`.
 * Arguments are everything inside the balanced outer parentheses.
 */
function parseRecipeString(recipe: string): ParsedOperation[] {
  const ops: ParsedOperation[] = [];
  let i = 0;
  const len = recipe.length;

  while (i < len) {
    // Skip whitespace between operations
    while (i < len && /\s/.test(recipe[i])) i++;
    if (i >= len) break;

    // Read operation name: word chars, underscores, slashes, hyphens
    const nameStart = i;
    while (i < len && /[A-Za-z0-9_/\-]/.test(recipe[i])) i++;
    const name = recipe.substring(nameStart, i);

    if (!name) break;

    // Expect opening paren
    if (i >= len || recipe[i] !== '(') {
      // Malformed — operation without args paren; skip this char and try again
      ops.push({ name, rawArgs: '' });
      continue;
    }
    i++; // skip '('

    // Find matching closing paren (handle nested parens and quoted strings)
    let depth = 1;
    const argsStart = i;
    while (i < len && depth > 0) {
      const ch = recipe[i];
      if (ch === '(') {
        depth++;
      } else if (ch === ')') {
        depth--;
      } else if (ch === "'" || ch === '"') {
        // Skip quoted string
        const quote = ch;
        i++;
        while (i < len && recipe[i] !== quote) {
          if (recipe[i] === '\\') i++; // skip escaped char
          i++;
        }
        // i now points at closing quote (or end)
      }
      if (depth > 0) i++;
    }

    const rawArgs = recipe.substring(argsStart, i);
    if (i < len) i++; // skip closing ')'

    ops.push({ name, rawArgs });
  }

  return ops;
}

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

/**
 * Parse a CyberChef URL and convert to our pipeline format.
 *
 * @param url — full CyberChef URL, or just the hash fragment
 * @returns parse result with pipeline + stats, or null if not a valid CyberChef URL
 */
export function parseCyberChefUrl(url: string): CyberChefParseResult | null {
  try {
    // Extract hash fragment
    let hash: string;
    const hashIdx = url.indexOf('#');
    if (hashIdx >= 0) {
      hash = url.slice(hashIdx + 1);
    } else {
      // Maybe just the hash content without #
      hash = url;
    }

    if (!hash.startsWith('recipe=')) return null;

    // Separate recipe from input: `recipe=...&input=base64data`
    let recipeRaw: string;
    let inputBase64: string | undefined;

    const inputIdx = hash.indexOf('&input=');
    if (inputIdx >= 0) {
      recipeRaw = hash.substring('recipe='.length, inputIdx);
      inputBase64 = hash.substring(inputIdx + '&input='.length);
    } else {
      recipeRaw = hash.substring('recipe='.length);
    }

    // URL-decode the recipe string
    const recipe = decodeURIComponent(recipeRaw);

    if (!recipe) return null;

    // Parse operations
    const parsedOps = parseRecipeString(recipe);
    if (parsedOps.length === 0) return null;

    // Map to our tools
    const steps: DeserializedPipeline['steps'] = [];
    const skippedOperations: string[] = [];

    for (const op of parsedOps) {
      const toolId = CYBERCHEF_TO_TOOL[op.name];
      if (toolId) {
        steps.push({
          toolId,
          config: {},
          label: op.name.replace(/_/g, ' '),
          isEnabled: true,
        });
      } else {
        // Use display-friendly name for the warning
        skippedOperations.push(op.name.replace(/_/g, ' '));
      }
    }

    // Decode input data if present
    let inputData: string | undefined;
    if (inputBase64) {
      try {
        inputData = atob(decodeURIComponent(inputBase64));
      } catch {
        // Input decode failed — proceed without it
        console.warn('[cyberchefImport] Failed to decode input data from CyberChef URL');
      }
    }

    const pipeline: DeserializedPipeline = {
      steps,
      name: 'CyberChef Import',
      inputData,
    };

    return {
      pipeline,
      mappedCount: steps.length,
      skippedOperations,
      totalOperations: parsedOps.length,
    };
  } catch (err) {
    console.warn('[cyberchefImport] Failed to parse CyberChef URL:', err);
    return null;
  }
}
